import { useEffect, useRef, useState } from "react";
import SockJS from "sockjs-client";
import {
  Client,
  IMessage
} from "@stomp/stompjs";

import {
  useNotificationContext
} from "../Context/NotificationContext";
import { storage } from "../Utils/authStorage";
import { WS_BASE_URL } from "../Services/ApiClient";


/** How long to wait before a dropped connection is retried. */
const RECONNECT_DELAY_MS = 30_000;

/**
 * The server rejects the STOMP connect when the token is missing or no longer valid. Retrying
 * with the same bad token just fills the server log with denied connections, so the client
 * stops once it sees one of these.
 */
const AUTH_FAILURE_PATTERN = /(invalid jwt|jwt token|unauthoriz|access denied|not permitted)/i;


export const useNotifications = () => {

  const { addNotification } = useNotificationContext();

  // The provider recreates the callback on every render. Keeping it in a ref lets the socket
  // stay connected instead of being torn down and rebuilt on each render.
  const addNotificationRef = useRef(addNotification);

  // Whether a session exists. The socket is tied to that, not to the token itself: the token
  // is rotated while the page is open and the socket reads the current one when it connects.
  const [hasToken, setHasToken] = useState<boolean>(
    () => !!storage.getToken()
  );

  useEffect(() => {
    addNotificationRef.current = addNotification;
  }, [addNotification]);

  useEffect(() => {

    const handleTokenChanged = (
      event: Event
    ) => {
      const token = (event as CustomEvent<{ token?: string | null }>)?.detail?.token;

      setHasToken(!!token);
    };

    window.addEventListener(
      "auth-token-changed",
      handleTokenChanged
    );

    return () =>
      window.removeEventListener(
        "auth-token-changed",
        handleTokenChanged
      );

  }, []);


  useEffect(() => {

    if (!hasToken) {
      // Nothing to authenticate with yet - the socket is opened once a session exists.
      return;
    }

    const client = new Client({

      webSocketFactory: () =>
        new SockJS(
          `${WS_BASE_URL}/ws`
        ),

      connectHeaders: {
        Authorization: `Bearer ${storage.getToken()}`
      },

      // Read the token at connect time. The access token is rotated while the page is open,
      // and a token captured when the hook mounted is stale by the next reconnect.
      beforeConnect: () => {
        const token = storage.getToken();

        if (token) {
          client.connectHeaders = {
            Authorization: `Bearer ${token}`
          };
        }
      },

      reconnectDelay: RECONNECT_DELAY_MS,

      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,


      onConnect: () => {
        console.log("STOMP CONNECTED");

        client.subscribe(
          "/user/queue/notifications",
          (message: IMessage) => {

            console.log(
              "RAW WS MESSAGE:",
              message.body
            );

            try {

              const notification = JSON.parse(message.body);

              addNotificationRef.current(notification);

              console.log(
                "PARSED NOTIFICATION:",
                notification
              );


            } catch (error) {

              console.error(
                "Notification parse error",
                error
              );

            }

          }
        );
      },


      onStompError: (
        frame
      ) => {

        const message = frame.headers["message"] ?? "";

        console.error(
          "STOMP Error:",
          message
        );

        if (AUTH_FAILURE_PATTERN.test(String(message))) {
          // The token was rejected. Reconnecting with it would fail the same way.
          client.deactivate();
        }

      },

      onWebSocketError: (
        event
      ) => {

        console.error(
          "WebSocket Error:",
          event
        );

      },

      onWebSocketClose: (
        event
      ) => {

        console.log(
          "WebSocket Closed",
          event.code
        );

      }

    });

    client.activate();

    return () => {
      client.deactivate();
    };

  }, [hasToken]);

};
